import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faComment, faTrash, faPause, faPlay, faPlus,
  faSpinner, faPaperPlane, faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import reportsService from '../services/reportsService';
import { useMessageAlert } from '../components/common/MessageAlert';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { UI_COLORS, colorHelpers } from '../utils/colors';

const TABS = [
  { id: 'recipients', label: 'Recipients' },
  { id: 'weekly', label: 'Weekly Business Report' },
  { id: 'ops', label: 'Daily Ops Alert' },
];

const Card = ({ title, subtitle, children, right }) => (
  <div className="bg-white rounded-xl border p-5 mb-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{title}</h2>
        {subtitle && <p className="text-sm mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

/** Tonnage is formatted here from the raw kg metrics — see the note on `metrics` below. */
const formatKg = (kg) => {
  const n = Number(kg);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return `${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} t`;
  return `${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })} kg`;
};

const formatMetric = (key, value) => {
  if (value === null || value === undefined) return '—';
  if (key.endsWith('_kg')) return formatKg(value);
  if (key.endsWith('_pct')) return `${Number(value).toFixed(1)}%`;
  return Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-IN') : String(value);
};

const humanize = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ---------------------------------------------------------------------------

const RecipientsTab = () => {
  const { showSuccess, showError } = useMessageAlert();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ channel: 'email', value: '', label: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsService.getRecipients();
      if (res.success) setData(res.data);
      else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.value.trim()) return;
    setAdding(true);
    try {
      const res = await reportsService.addRecipient(form);
      if (res.success) {
        setForm({ channel: form.channel, value: '', label: '' });
        // Re-read from the server: it normalizes the value (email lowercased, mobile cut
        // to its last 10 digits), so echoing what was typed would show the wrong thing.
        await load();
        showSuccess('Recipient added');
      } else {
        showError(res.error);
      }
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const toggleActive = async (r) => {
    setBusyId(r.id);
    try {
      const res = await reportsService.setRecipientActive(r.id, !r.is_active);
      if (res.success) load(); else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    const r = confirmDelete;
    setConfirmDelete(null);
    setBusyId(r.id);
    try {
      const res = await reportsService.deleteRecipient(r.id);
      if (res.success) { load(); showSuccess('Recipient removed'); } else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const renderColumn = (channel, list, activeCount, icon) => (
    <div className="flex-1 min-w-[260px]">
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={icon} style={{ color: UI_COLORS.TEXT.SECONDARY }} />
        <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {channel === 'email' ? 'Email' : 'WhatsApp'}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{
            color: activeCount > 0 ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED,
            backgroundColor: colorHelpers.withOpacity(
              activeCount > 0 ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.TEXT.MUTED, 0.12),
          }}
        >
          {activeCount} active
        </span>
      </div>
      {list.length === 0 ? (
        <p className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>No recipients yet.</p>
      ) : list.map((r) => (
        <div
          key={r.id}
          className="flex items-center justify-between gap-2 border-t py-2"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT, opacity: r.is_active ? 1 : 0.5 }}
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.value}</div>
            {r.label && <div className="text-xs truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>{r.label}</div>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button" disabled={busyId === r.id} onClick={() => toggleActive(r)}
              title={r.is_active ? 'Pause' : 'Resume'}
              className="p-2 rounded-lg disabled:opacity-40"
              style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
            >
              <FontAwesomeIcon icon={r.is_active ? faPause : faPlay} className="text-xs" />
            </button>
            <button
              type="button" disabled={busyId === r.id} onClick={() => setConfirmDelete(r)}
              title="Remove"
              className="p-2 rounded-lg disabled:opacity-40"
              style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
            >
              <FontAwesomeIcon icon={faTrash} className="text-xs" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Card
        title="Report recipients"
        subtitle="Who receives the weekly business report. A channel can still be switched off globally on the server, so recipients alone do not guarantee delivery."
      >
        {loading ? (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        ) : (
          <div className="flex flex-wrap gap-8">
            {renderColumn('email', data?.email || [], data?.email_active_count ?? 0, faEnvelope)}
            {renderColumn('whatsapp', data?.whatsapp || [], data?.whatsapp_active_count ?? 0, faComment)}
          </div>
        )}
      </Card>

      <Card title="Add recipient">
        <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Channel</label>
            <select
              value={form.channel}
              onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
              className="rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
            >
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
              {form.channel === 'email' ? 'Email address' : 'Mobile number'}
            </label>
            <input
              type="text" value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              placeholder={form.channel === 'email' ? 'name@company.com' : '9876543210'}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>Label (optional)</label>
            <input
              type="text" value={form.label} maxLength={120}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
            />
          </div>
          <button
            type="submit" disabled={adding || !form.value.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
          >
            <FontAwesomeIcon icon={adding ? faSpinner : faPlus} className={adding ? 'animate-spin' : ''} />
            Add
          </button>
        </form>
      </Card>

      {confirmDelete && (
        <ConfirmDialog
          isOpen
          title="Remove recipient"
          message={`Stop sending reports to ${confirmDelete.value}?`}
          confirmLabel="Remove"
          cancelLabel="Cancel"
          confirmVariant="danger"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------

const WeeklyTab = () => {
  const { showSuccess, showError } = useMessageAlert();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pdfEnabled, setPdfEnabled] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([reportsService.previewWeekly(), reportsService.getSettings()]);
      if (p.success) setPreview(p.data); else showError(p.error);
      if (s.success) {
        const row = (s.data?.settings || []).find((x) => x.key === 'weekly_report_pdf');
        setPdfEnabled(!!row?.enabled);
      }
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePdf = async () => {
    const next = !pdfEnabled;
    setPdfBusy(true);
    try {
      const res = await reportsService.setSetting('weekly_report_pdf', next);
      if (res.success) { setPdfEnabled(next); showSuccess(next ? 'PDF will be attached' : 'PDF attachment off'); }
      else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await reportsService.sendWeeklyNow();
      if (!res.success) { showError(res.error); return; }
      const d = res.data || {};
      const bits = [
        `Email: ${d.email?.sent ?? 0} sent, ${d.email?.failed ?? 0} failed${d.email?.skipped_no_smtp ? ' (SMTP off)' : ''}`,
        `WhatsApp: ${d.whatsapp?.sent ?? 0} sent, ${d.whatsapp?.failed ?? 0} failed${d.whatsapp?.skipped_disabled ? ' (disabled)' : ''}`,
      ];
      if (d.pdf_attached) bits.push('PDF attached');
      showSuccess(bits.join(' · '));
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const metrics = preview?.metrics || {};

  return (
    <>
      <Card
        title="Weekly business report"
        subtitle={preview ? `Covering ${preview.week_start} → ${preview.week_end}` : 'Sent every Monday 01:00 IST for the previous Mon–Sun.'}
        right={
          <button
            type="button" onClick={sendNow} disabled={sending || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
            style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
          >
            <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} className={sending ? 'animate-spin' : ''} />
            Send now
          </button>
        }
      >
        <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          <input type="checkbox" checked={pdfEnabled} disabled={pdfBusy} onChange={togglePdf} className="w-4 h-4" />
          <FontAwesomeIcon icon={faFilePdf} style={{ color: UI_COLORS.TEXT.MUTED }} />
          Attach a PDF to the weekly email
        </label>

        {loading ? (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        ) : (
          <>
            {preview?.email_subject && (
              <p className="text-sm mb-4" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                <span className="font-semibold">Subject:</span> {preview.email_subject}
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(metrics)
                .filter(([, v]) => !Array.isArray(v))
                .map(([k, v]) => (
                  <div key={k} className="rounded-lg border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div className="text-xs mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{humanize(k)}</div>
                    <div className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formatMetric(k, v)}</div>
                  </div>
                ))}
            </div>

            {['top_customers', 'top_products'].map((key) => (
              Array.isArray(metrics[key]) && metrics[key].length > 0 && (
                <div key={key} className="mt-5">
                  <h3 className="text-sm font-bold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{humanize(key)}</h3>
                  <div className="rounded-lg border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    {metrics[key].map(([name, kg], i) => (
                      <div key={i} className="flex justify-between px-3 py-2 border-t text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <span>{name}</span>
                        <span className="font-semibold">{formatKg(kg)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </>
        )}
      </Card>
    </>
  );
};

// ---------------------------------------------------------------------------

const OpsAlertTab = () => {
  const { showSuccess, showError } = useMessageAlert();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [force, setForce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsService.previewOpsAlert();
      if (res.success) setPreview(res.data); else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  // showError is intentionally NOT a dependency: useMessageAlert returns a fresh
  // function identity on every render, so including it rebuilds `load`, which
  // re-fires the effect, which re-renders — an endless fetch loop.
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await reportsService.sendOpsAlertNow(force);
      if (res.success) showSuccess(preview?.has_alert || force ? 'Ops alert sent' : 'Nothing to send — the day is clean');
      else showError(res.error);
    } catch (err) {
      showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      title="Daily ops alert"
      subtitle="Aging-challan backlog only. Stock alerts are deliberately excluded."
      right={
        <button
          type="button" onClick={sendNow} disabled={sending || loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
          style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
        >
          <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} className={sending ? 'animate-spin' : ''} />
          Send now
        </button>
      }
    >
      {loading ? (
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
      ) : (
        <>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="rounded-lg border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <div className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>Backlog</div>
              <div className="text-lg font-bold">{preview?.backlog_count ?? 0}</div>
            </div>
            <div className="rounded-lg border p-3" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <div className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>Oldest (days)</div>
              <div className="text-lg font-bold">{preview?.backlog_oldest_days ?? 0}</div>
            </div>
          </div>

          {preview?.has_alert ? (
            <div className="rounded-lg border p-3 text-sm" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              {(preview.lines || []).map((line, i) => <div key={i}>{line}</div>)}
            </div>
          ) : (
            <p className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
              Nothing is overdue today, so the scheduled alert stays silent.
            </p>
          )}

          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="w-4 h-4" />
            Force send even on a clean day (for testing)
          </label>
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------

const ReportsPage = () => {
  const [tab, setTab] = useState('recipients');

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Scheduled Reports</h1>
        <p className="mt-2 text-gray-600">
          Weekly business report and daily ops alert — recipients, delivery and previews.
          Volume and counts only; these reports carry no financial or stock figures.
        </p>
      </div>

      <div className="flex gap-1 mb-5 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
        {TABS.map((tb) => (
          <button
            key={tb.id} type="button" onClick={() => setTab(tb.id)}
            className="px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px"
            style={{
              color: tab === tb.id ? UI_COLORS.BRAND.TERTIARY : UI_COLORS.TEXT.SECONDARY,
              borderColor: tab === tb.id ? UI_COLORS.BRAND.TERTIARY : 'transparent',
            }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'recipients' && <RecipientsTab />}
      {tab === 'weekly' && <WeeklyTab />}
      {tab === 'ops' && <OpsAlertTab />}
    </div>
  );
};

export default ReportsPage;
